import { useState } from 'react';
import { CopyButton } from '../../components/CopyButton';
import { getErrorMessage } from '../../lib/errors';
import type { OnboardingParseRead } from '../../types/api';
import { parseBrief } from './accountApi';
import { BRIEF_TEXT_CAP } from './brandBrief';
import { brandKindCopy, type BrandKind } from './brandKind';

interface PasteBriefStepProps {
  kind: BrandKind;
  onParsed: (result: OnboardingParseRead) => void;
  onBack: () => void;
}

/**
 * The paste path: a labelled template, filled in here or anywhere else and
 * pasted back. The server extracts the fields with a label table and a regex,
 * no model, and the user checks the result on the form. The template follows
 * the kind; the parser reads both sets of labels either way.
 */
export function PasteBriefStep({ kind, onParsed, onBack }: PasteBriefStepProps) {
  const copy = brandKindCopy(kind);
  const [text, setText] = useState(copy.template);
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const labels = [
    copy.name.label,
    copy.headline?.label,
    copy.industry.label,
    copy.valueProposition.label,
    copy.targetAudience.label,
    copy.tone.label,
    copy.tagline.label,
    'Never say',
    'Platforms',
    'Goals',
  ]
    .filter((label): label is string => Boolean(label))
    .join(', ');

  async function handleParse() {
    if (!text.trim()) {
      setError('Paste or fill in the brief first.');
      return;
    }
    setError(null);
    setIsParsing(true);
    try {
      onParsed(await parseBrief(text));
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not read the brief. Try again.'));
    } finally {
      setIsParsing(false);
    }
  }

  return (
    <section className="wizard">
      <div className="wizard__step">
        <h2 className="wizard__title">
          {kind === 'personal' ? 'Paste your brief' : 'Paste your brand brief'}
        </h2>
        <p className="wizard__lede">
          Fill in the labels below, or copy the template into your notes or a chat assistant, fill
          it there and paste it back. Every line is optional except the name. We read it and show
          you the result before anything is saved.
        </p>

        <label className="field">
          <span className="field__label">Brief</span>
          <textarea
            className="field__input brief-paste"
            rows={14}
            maxLength={BRIEF_TEXT_CAP}
            spellCheck={false}
            value={text}
            onChange={(event) => {
              setError(null);
              setText(event.target.value);
            }}
          />
          <span className="field__hint">
            Labels we understand: {labels}. Lists split on commas or new lines.
          </span>
        </label>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="wizard__actions">
        <div className="brief-paste__secondary">
          <button className="button button--ghost" type="button" onClick={onBack}>
            Back
          </button>
          <CopyButton text={copy.template} label="Copy template" />
        </div>
        <button
          className="button button--primary"
          type="button"
          onClick={() => void handleParse()}
          disabled={isParsing}
        >
          {isParsing ? 'Reading brief' : 'Read the brief'}
        </button>
      </div>
    </section>
  );
}
